import { MSG } from '../content/messages.ts';
import {
  formatBRL,
  formatDateBr,
  formatDateTimeBr,
  formatMonthBr,
  MoneyError,
  parseDecimalToCents,
} from '@ana/domain';

export const brl = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '—' : formatBRL(cents);

export const qty = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });

export const pct = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined || !Number.isFinite(v)
    ? 'N/D'
    : `${v.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

export const ratioPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? 'N/D' : pct(v * 100);

/** Duration in hours with one decimal ("6,0 h"); null → "N/D" (nothing measured), never "0 h". */
export const hoursBr = (h: number | null | undefined): string =>
  h === null || h === undefined || !Number.isFinite(h)
    ? 'N/D'
    : `${h.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;

// Dates and times delegate to the single formatter in @ana/domain (datetime-br.ts); '—' when absent or invalid.
export const dateBr = (iso: string | null | undefined): string => formatDateBr(iso) ?? '—';

/** Instant in Brasília time: DD/MM/AAAA HH:mm:ss (24 h, with seconds, no comma). */
export const dateTimeBr = (iso: string | null | undefined): string => formatDateTimeBr(iso) ?? '—';

/** Competence month (AAAA-MM or AAAA-MM-DD) → MM/AAAA. */
export const monthBr = (iso: string | null | undefined): string => formatMonthBr(iso) ?? '—';

/** Text typed by the user → centavos. Empty = null ("não preenchido"), never 0. */
export function parseMoneyInput(
  text: string,
): { ok: true; cents: number | null } | { ok: false; error: string } {
  const t = text.trim();
  if (t === '') return { ok: true, cents: null };
  try {
    const cents = parseDecimalToCents(t, ',');
    if (cents < 0) return { ok: false, error: MSG.validation.negative };
    return { ok: true, cents };
  } catch (e) {
    return { ok: false, error: e instanceof MoneyError ? e.message : MSG.validation.money };
  }
}

export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return formatBRL(cents).replace('R$ ', '');
}

export function parseQtyInput(
  text: string,
): { ok: true; value: number | null } | { ok: false; error: string } {
  const t = text.trim().replace(/\./g, '');
  if (t === '') return { ok: true, value: null };
  if (!/^\d+$/.test(t)) return { ok: false, error: MSG.validation.quantity };
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n > 10_000_000) return { ok: false, error: MSG.validation.quantityRange };
  return { ok: true, value: n };
}

export const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  CLOSED: 'Fechado',
  CHECKED: 'Conferido',
  APPROVED: 'Aprovado',
  RETURNED: 'Devolvido',
  REOPEN_PENDING: 'Reabertura solicitada',
  REOPENED: 'Reaberto',
  CANCELLED: 'Cancelado',
  NOT_STARTED: 'Aguardando',
  PENDING_STATEMENT: 'Pendente de extrato',
  PENDING_MOVEMENT: 'Pendente de lançamento',
  PARTIAL: 'Parcial',
  MATCHED: 'Conciliado',
  TOLERABLE_DIFFERENCE: 'Diferença tolerável',
  DIVERGENT: 'Divergência',
  RESOLVED: 'Resolvido',
  PENDING_REVIEW: 'Débito a analisar',
  ABERTA: 'Aberta',
  EM_ANALISE: 'Em análise',
  JUSTIFICADA: 'Justificada',
  APROVADA: 'Aprovada',
  ENCERRADA: 'Encerrada',
  PREVIEW: 'Pré-visualização',
  COMMITTED: 'Importado',
  DISCARDED: 'Descartado',
  OPEN: 'Aberto',
  ACKNOWLEDGED: 'Reconhecido',
  COMPARABLE: 'Comparável',
  NOT_COMPARABLE: 'Não comparável',
  NO_BASE: 'Sem base',
};

/** Confirmation shown after a workflow transition succeeded on the server. */
export const TRANSITION_DONE: Record<string, string> = {
  close: 'Dia fechado.',
  check: 'Dia conferido.',
  return: 'Dia devolvido para correção.',
  approve: 'Dia aprovado.',
  request_reopen: 'Reabertura solicitada.',
  authorize_reopen: 'Reabertura autorizada.',
  reject_reopen: 'Reabertura negada.',
  cancel: 'Rascunho cancelado.',
};

export const TRANSITION_LABEL: Record<string, string> = {
  close: 'Fechar dia',
  check: 'Conferir',
  return: 'Devolver',
  approve: 'Aprovar',
  request_reopen: 'Solicitar reabertura',
  authorize_reopen: 'Autorizar reabertura',
  reject_reopen: 'Negar reabertura',
  cancel: 'Cancelar rascunho',
};

export const EXCEPTION_KIND_LABEL: Record<string, string> = {
  CASH_DIFFERENCE: 'Diferença de caixa',
  RECONCILIATION: 'Conciliação',
  MISSING_DATA: 'Dado ausente',
  IMPORT: 'Importação',
  OTHER: 'Outra',
};

export const ALERT_TYPE_LABEL: Record<string, string> = {
  DADOS_OBRIGATORIOS: 'Dados obrigatórios',
  CONCILIACAO: 'Conciliação',
  EXTRATO: 'Extrato',
  APROVACAO: 'Aprovação',
  RECEITA: 'Receita',
  CAIXA: 'Caixa',
  OPERACIONAL: 'Operacional',
  OFFLINE: 'Sincronização',
  INTEGRIDADE: 'Integridade',
};

export const KIND_LABEL: Record<string, string> = {
  STATION: 'Estações de pagamento',
  AUTO_CHANNEL: 'Recebimentos automáticos',
  AGREEMENT: 'Convênios e outros recebimentos',
  CARD_BRAND: 'Cartões (Sitef)',
  NON_TARIFFED: 'Acessos não tarifados',
  SPECIAL: 'Categorias especiais',
  OTHER_REVENUE: 'Outras receitas',
};
